import { useState } from 'react';

interface GasDetectionData {
  id: string;
  location: string;
  gasType: string;
  concentration: number;
  threshold: number;
  detectTime: string;
  status: string;
}

interface EmergencyPlan {
  id: string;
  eventType: string;
  level: string;
  responseTeam: string;
  estimatedTime: string;
  status: string;
}

export default function EmergencyResponse() {
  const [gasData] = useState<GasDetectionData[]>([
    { id: 'G001', location: '危险品仓库A区', gasType: '甲烷', concentration: 0.8, threshold: 1.0, detectTime: '2025-01-15 09:30', status: '正常' },
    { id: 'G002', location: '油罐区3号', gasType: '硫化氢', concentration: 12, threshold: 10, detectTime: '2025-01-15 10:15', status: '超标' },
    { id: 'G003', location: '化工码头', gasType: '一氧化碳', concentration: 15, threshold: 20, detectTime: '2025-01-15 11:00', status: '正常' },
    { id: 'G004', location: '危险品仓库B区', gasType: '氨气', concentration: 28, threshold: 25, detectTime: '2025-01-15 08:45', status: '超标' },
  ]);

  const [plans] = useState<EmergencyPlan[]>([
    { id: 'P001', eventType: '危险品泄漏', level: '二级', responseTeam: '应急小组A', estimatedTime: '15分钟', status: '待命' },
    { id: 'P002', eventType: '火灾事故', level: '一级', responseTeam: '消防队', estimatedTime: '10分钟', status: '待命' },
    { id: 'P003', eventType: '人员落水', level: '三级', responseTeam: '救援队B', estimatedTime: '20分钟', status: '待命' },
  ]);

  const normalCount = gasData.filter(g => g.status === '正常').length;
  const exceedCount = gasData.filter(g => g.status === '超标').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">突发环境事件应急</h2>
        <button className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-alarm-warning-line mr-2"></i>启动应急预案
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">监测点位</span>
            <i className="ri-radar-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{gasData.length}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">正常点位</span>
            <i className="ri-checkbox-circle-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-teal-400">{normalCount}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">超标点位</span>
            <i className="ri-error-warning-line text-red-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-red-400">{exceedCount}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">应急预案</span>
            <i className="ri-file-list-3-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{plans.length}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">事故区域探测</h3>
          <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
            <img 
              src="https://public.readdy.ai/ai/img_res/85d267ca39678b535952e15b16c2cc73.jpg"
              alt="气体探测"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
              <i className="ri-alert-line mr-2"></i>
              气体超标
            </div>
            <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-xs">
              无人机-12
            </div>
            <div className="absolute bottom-4 left-4 right-4 bg-slate-900/80 backdrop-blur-sm rounded-lg p-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">检测气体:</span>
                  <span className="text-white ml-2">硫化氢</span>
                </div>
                <div>
                  <span className="text-slate-400">浓度:</span>
                  <span className="text-red-400 ml-2 font-bold">12 ppm</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">气体浓度趋势</h3>
          <div className="space-y-4">
            {gasData.map((item) => (
              <div key={item.id}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">{item.gasType} - {item.location}</span>
                  <span className={`text-sm font-medium ${item.status === '超标' ? 'text-red-400' : 'text-teal-400'}`}>
                    {item.concentration} / {item.threshold}
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${item.status === '超标' ? 'bg-red-500' : 'bg-teal-500'}`}
                    style={{ width: `${Math.min((item.concentration / item.threshold) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">气体探测数据</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">位置</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">气体类型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">浓度</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">状态</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {gasData.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-slate-300">{item.location}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{item.gasType}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">
                      {item.concentration} / {item.threshold}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        item.status === '正常' ? 'bg-teal-500/20 text-teal-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">应急预案</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">事件类型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">响应等级</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">响应团队</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">预计时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-slate-300">{plan.eventType}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        plan.level === '一级' ? 'bg-red-500/20 text-red-400' :
                        plan.level === '二级' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {plan.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{plan.responseTeam}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">{plan.estimatedTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
